import type { AddressComponents, AddressPrediction } from '../hooks/useAddressAutocomplete';

const API_KEY = import.meta.env.VITE_GOOGLE_MAP_API_KEY as string;
const PLACES_URL = 'https://places.googleapis.com/v1/places';

interface PlaceSuggestion {
  placePrediction: {
    placeId: string;
    text: { text: string };
  };
}

interface PlaceAddressComponent {
  longText: string;
  types: string[];
}

const getComponent = (
  components: PlaceAddressComponent[],
  type: string
): string => components.find((c) => c.types.includes(type))?.longText ?? '';

export const placesApiService = {
  async autocomplete(input: string): Promise<AddressPrediction[]> {
    const res = await fetch(`${PLACES_URL}:autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
      },
      body: JSON.stringify({
        input,
        includedRegionCodes: ['ro'],
        languageCode: 'ro',
        includedPrimaryTypes: ['(regions)'],
      }),
    });

    const data = await res.json() as { suggestions?: PlaceSuggestion[] };

    return (data.suggestions ?? []).map((s) => ({
      placeId: s.placePrediction.placeId,
      description: s.placePrediction.text.text,
    }));
  },

  async getAddressComponents(placeId: string): Promise<AddressComponents> {
    const res = await fetch(`${PLACES_URL}/${placeId}`, {
      headers: {
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': 'addressComponents',
      },
    });

    const data = await res.json() as { addressComponents?: PlaceAddressComponent[] };
    const components = data.addressComponents ?? [];

    return {
      county: getComponent(components, 'administrative_area_level_1'),
      locality:
        getComponent(components, 'locality') ||
        getComponent(components, 'administrative_area_level_2'),
      localityType: '',
      street: getComponent(components, 'route'),
      streetNumber: getComponent(components, 'street_number'),
      building: '',
      entrance: '',
      floor: '',
      apartment: '',
      sector: '',
    };
  },
};
